import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    styled,
    Typography,
} from '@mui/material';
import { AddressItemProps } from '../../models/Addresses';
import useProfileEditorDialog from '../../hooks/useProfileEditorDialog';
import EditIcon from '@mui/icons-material/Edit';
import AddressService from '../../services/AddressService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import useConfirmDialog from '../../hooks/dialogs/useConfirmDialog';

const CardWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '10px',
    '> *': {
        flex: 1,
    },
    [theme.breakpoints.down('md')]: {
        flexWrap: 'wrap',
        '> *': {
            flex: 'auto',
        },
    },
    [theme.breakpoints.down('sm')]: {
        '> *': {
            width: '100%',
        },
    },
}));

const AddressTitleWrapper = styled(Box)(() => ({
    padding: '25px 0 10px',
    display: 'flex',
    alignItems: 'center',
}));

export const AddressItem: FC<AddressItemProps> = (props) => {
    const { t } = useTranslation();
    const profileDialog = useProfileEditorDialog();
    const address = props.address;
    const confirmDialog = useConfirmDialog();

    const openDialog = (type, data) => {
        if (data) {
            profileDialog.setSelectedAddress({
                type: type,
                id: data.id,
                data:
                    type == 'shipping'
                        ? data.shippingAddress
                        : data.billingAddress,
            });
        }
        profileDialog.showAddressesEditor(true);
    };

    const handleDeleteProfile = () => {
        AddressService.deleteAddresses(address.id).then((res) => {
            SnackbarUtils.success(t('NOTIFICATION.ADDRESS_DELETED').toString());
            props.handleChange();
        });
    };

    const deleteAddress = () => {
        confirmDialog.showDialog({
            title: t('PROFILE.ADDRESS_DELETE_CONFIRM_DIALOG.TITLE'),
            content: t('PROFILE.ADDRESS_DELETE_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('PROFILE.ADDRESS_DELETE_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('PROFILE.ADDRESS_DELETE_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => handleDeleteProfile(),
        });
    };

    return (
        <>
            <Box>
                <AddressTitleWrapper>
                    <Typography variant="h3" sx={{ paddingRight: '10px' }}>
                        {t('PROFILE.ADDRESS_CONTENT.LOCATION')}: {address.name}
                    </Typography>
                    <Button onClick={(e) => deleteAddress()}>
                        <DeleteIcon fontSize={'small'} />
                    </Button>
                </AddressTitleWrapper>
                <CardWrapper>
                    <Card>
                        <CardHeader
                            title={t('PROFILE.ADDRESS_CONTENT.SHIPPING_TITLE')}
                            action={
                                <EditIcon
                                    fontSize={'small'}
                                    onClick={(e) =>
                                        openDialog('shipping', address)
                                    }
                                    sx={{ cursor: 'pointer' }}
                                />
                            }
                        />
                        <CardContent>
                            <Typography>
                                {address.shippingAddress.postcode}{' '}
                                {address.shippingAddress.city}
                            </Typography>
                            <Typography>
                                {address.shippingAddress.address1}
                            </Typography>
                            <Typography>
                                {address.shippingAddress.address2}
                            </Typography>
                            <Typography>
                                {t(
                                    'COUNTRIES.' +
                                        address.shippingAddress.country,
                                    { ns: 'countries' }
                                )}
                            </Typography>
                            <Typography>
                                {address.shippingAddress.phone}
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader
                            title={t('PROFILE.ADDRESS_CONTENT.BILLING_TITLE')}
                            // action={
                            //     <EditIcon
                            //         fontSize={'small'}
                            //         onClick={(e) =>
                            //             openDialog('billing', address)
                            //         }
                            //         sx={{ cursor: 'pointer' }}
                            //     />
                            // }
                        />
                        <CardContent>
                            <Typography>
                                {address.billingAddress.postcode}{' '}
                                {address.billingAddress.city}
                            </Typography>
                            <Typography>
                                {address.billingAddress.address1}
                            </Typography>
                            <Typography>
                                {address.billingAddress.address2}
                            </Typography>
                            <Typography>
                                {t(
                                    'COUNTRIES.' +
                                        address.billingAddress.country,
                                    { ns: 'countries' }
                                )}
                            </Typography>
                            <Typography>
                                {address.billingAddress.phone}
                            </Typography>
                        </CardContent>
                    </Card>
                </CardWrapper>
            </Box>
        </>
    );
};
